import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { format, addHours, setHours, setMinutes } from 'date-fns';
import { meetingsService } from '@/services/meetingsService';
import { useAuth } from '@/contexts/AuthContext';
import { onboardingService } from '@/services/onboardingService';

interface UseCalendarProps {
  defaultTitle: string;
  effectiveDuration: number;
}

export function useCalendar({
  defaultTitle,
  effectiveDuration,
}: UseCalendarProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const userId = user?.id;

  const [date, setDate] = useState<Date | undefined>(new Date());
  const [time, setTime] = useState<string>('09:00');
  const [hasCalendar, setHasCalendar] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [hasConflict, setHasConflict] = useState<boolean>(false);
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Check if user has calendar connected
  useEffect(() => {
    const checkCalendar = async () => {
      if (!userId) return;

      try {
        const integrations = await meetingsService.getCalendarIntegrations();
        const connected = Array.isArray(integrations) && integrations.length > 0;
        setHasCalendar(connected);

        if (connected) {
          const providers = integrations.map((i: any) => i.provider);
          setConnectedProviders(providers);
        }
      } catch (error) {
        console.error('Error checking calendar connection:', error);
        setHasCalendar(false);
        setConnectionError('Error checking calendar connection status');
      }
    };

    checkCalendar();
  }, [userId]);

  // Parse time string to hours and minutes
  const getTimeComponents = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return { hours, minutes };
  };

  // Calculate start and end time
  const getEventTimes = () => {
    if (!date) return { startTime: null, endTime: null };

    const { hours, minutes } = getTimeComponents(time);
    const startTime = setMinutes(setHours(new Date(date), hours), minutes);
    const endTime = addHours(startTime, effectiveDuration / 60);

    return { startTime, endTime };
  };

  // Check for calendar conflicts
  const checkConflicts = useCallback(async () => {
    if (!date || !userId) return;

    setIsChecking(true);
    setHasConflict(false);

    try {
      const { startTime, endTime } = getEventTimes();
      if (!startTime || !endTime) return;

      // Get calendar events for the day
      const events = await meetingsService.getCalendarEvents({
        startDate: format(startTime, 'yyyy-MM-dd'),
        endDate: format(endTime, 'yyyy-MM-dd'),
      });

      // Check for conflicts - only consider busy events
      if (Array.isArray(events)) {
        console.log('🔍 Checking conflicts:', {
          requestedStart: startTime.toISOString(),
          requestedEnd: endTime.toISOString(),
          eventsCount: events.length,
          events: events.map((e: any) => ({
            title: e.title,
            start: e.start_time,
            end: e.end_time,
            busy: e.busy
          }))
        });

        const conflict = events.some((event: any) => {
          // Only consider busy events - if busy is undefined/null, treat as busy=true
          if (event.busy === false) return false;
          
          const eventStart = new Date(event.start_time);
          const eventEnd = new Date(event.end_time);
          
          // Check if events overlap - using proper overlap logic
          // Two events overlap if: requestedStart < eventEnd && requestedEnd > eventStart
          const hasOverlap = startTime < eventEnd && endTime > eventStart;
          
          if (hasOverlap) {
            console.log('⚠️ Conflict detected:', {
              eventTitle: event.title,
              eventStart: eventStart.toISOString(),
              eventEnd: eventEnd.toISOString(),
              requestedStart: startTime.toISOString(),
              requestedEnd: endTime.toISOString()
            });
          }
          
          return hasOverlap;
        });

        console.log('✅ Conflict check result:', { hasConflict: conflict });
        setHasConflict(conflict);
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
    } finally {
      setIsChecking(false);
    }
  }, [date, time, userId, effectiveDuration]);

  // Auto-check conflicts when date/time changes - Real-time conflict checking
  useEffect(() => {
    if (!hasCalendar || !date) {
      setHasConflict(false);
      setIsChecking(false);
      return;
    }

    // Debounce to avoid too many API calls when user is changing date/time
    const timer = setTimeout(() => {
      checkConflicts();
    }, 500); // Debounce 500ms

    return () => clearTimeout(timer);
  }, [date, time, hasCalendar, checkConflicts]);

  // Schedule focus time
  const scheduleFocusTime = async () => {
    if (!date || !userId) {
      toast({
        title: 'Error',
        description: 'Please select a date',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const { startTime, endTime } = getEventTimes();
      if (!startTime || !endTime) {
        throw new Error('Invalid date/time');
      }

      // Create calendar event via backend (using calendar events endpoint, not meetings)
      const event = await meetingsService.createCalendarEvent({
        title: defaultTitle,
        description: 'Scheduled focus time block',
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        busy: true,
        reminder_minutes: 15,
      });

      // Trigger milestone check
      try {
        await onboardingService.completeMilestone('calendar_integration');
      } catch (error) {
        console.error('Error updating milestone:', error);
      }

      toast({
        title: 'Success',
        description: `Focus time scheduled for ${format(startTime, 'MMM d, yyyy at h:mm a')}`,
      });

      return event;
    } catch (error: any) {
      console.error('Error scheduling focus time:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to schedule focus time',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Connect calendar provider
  const connectCalendar = async (provider: 'google' | 'microsoft' | 'apple') => {
    try {
      setIsLoading(true);
      setConnectionError(null);

      const result = await meetingsService.connectCalendar({ provider });

      // If OAuth flow is required, redirect
      if (result.authUrl) {
        window.location.href = result.authUrl;
        return;
      }

      setHasCalendar(true);
      setConnectedProviders([...connectedProviders, provider]);

      toast({
        title: 'Calendar Connected',
        description: `Successfully connected ${provider} calendar`,
      });
    } catch (error: any) {
      console.error('Error connecting calendar:', error);
      setConnectionError(error.message || 'Failed to connect calendar');
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to connect calendar',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    date,
    setDate,
    time,
    setTime,
    hasCalendar,
    isLoading,
    isChecking,
    hasConflict,
    connectedProviders,
    connectionError,
    scheduleFocusTime,
    checkConflicts,
    connectCalendar,
  };
}
